import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2, Upload, X, AlertCircle, FileSpreadsheet, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYGCompact } from "../../utils/format";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

const TARGET_LABELS = { nombre: "Nombre", cedula: "Cédula", n_ips: "N° IPS", dias_reportados: "Días reportados *", monto_aporte: "Monto aporte" };
const ESTADO_BADGE = {
  ok: "bg-emerald-100 text-emerald-700", diferencia: "bg-amber-100 text-amber-700",
  no_reportado_ips: "bg-red-100 text-red-600", sin_partes: "bg-gray-100 text-gray-500",
};
const ESTADO_LABEL = { ok: "OK", diferencia: "Diferencia", no_reportado_ips: "Sin aporte IPS", sin_partes: "Sin partes diarios" };

function ImportIpsModal({ open, mes, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload");
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setStep("upload"); setHeaders([]); setMapping({}); setPreview(null); };
  const handleClose = () => { reset(); onClose(); };

  const doPreview = async (mapOverride) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (mapOverride) fd.append("column_mapping", JSON.stringify(mapOverride));
      const res = await axios.post(`${API}/hr/ips/import/preview`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.needs_mapping) { setHeaders(res.data.headers || []); setMapping(res.data.auto_mapping || {}); setStep("mapping"); }
      else { setPreview(res.data); setStep("preview"); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al leer el archivo"); }
    finally { setLoading(false); }
  };

  const confirmMapping = () => {
    if (mapping.dias_reportados === undefined || !(mapping.nombre !== undefined || mapping.cedula !== undefined || mapping.n_ips !== undefined)) {
      toast.error("Mapeá al menos Días reportados y (Nombre, Cédula o N° IPS)"); return;
    }
    doPreview(mapping);
  };

  const handleConfirmImport = async () => {
    if (!preview?.valid_rows?.length) { toast.error("No hay filas válidas para importar"); return; }
    setLoading(true);
    try {
      const rows = preview.valid_rows.map(r => ({ nombre: r.nombre, cedula: r.cedula, n_ips: r.n_ips, dias_reportados: r.dias_reportados, monto_aporte: r.monto_aporte }));
      const res = await axios.post(`${API}/hr/ips/import/confirm`, { mes, rows }, { withCredentials: true });
      toast.success(`Extracto importado: ${res.data.items_count} registros`);
      onDone(); handleClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al confirmar importación"); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="import-ips-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-ips-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-base font-bold text-gray-900">Importar extracto de IPS — {mes}</h2>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="import-ips-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {step === "upload" && (
            <>
              <p className="text-sm text-gray-500">Subí el extracto de aportes de IPS (Excel o CSV) del mes {mes}.</p>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0] || null)} className={inputCls} data-testid="import-ips-file-input" />
              <button onClick={() => doPreview(null)} disabled={!file || loading} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2" data-testid="import-ips-analyze-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Analizar Archivo
              </button>
            </>
          )}
          {step === "mapping" && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-start gap-2">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> No se detectaron todas las columnas. Mapeá cada campo.
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(TARGET_LABELS).map(([target, label]) => (
                  <div key={target}>
                    <label className={labelCls}>{label}</label>
                    <select value={mapping[target] ?? ""} onChange={e => setMapping(m => ({ ...m, [target]: e.target.value === "" ? undefined : Number(e.target.value) }))} className={inputCls} data-testid={`import-ips-mapping-${target}`}>
                      <option value="">— sin mapear —</option>
                      {headers.map((h, idx) => <option key={idx} value={idx}>{h || `Columna ${idx + 1}`}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <button onClick={confirmMapping} disabled={loading} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm" data-testid="import-ips-mapping-confirm-btn">
                {loading ? <Loader2 size={16} className="animate-spin inline" /> : "Continuar con este mapeo"}
              </button>
            </>
          )}
          {step === "preview" && preview && (
            <>
              <div className="flex gap-3 text-xs">
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-semibold">{preview.summary.valid} filas válidas</span>
                {preview.summary.errors > 0 && <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full font-semibold">{preview.summary.errors} errores</span>}
              </div>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="px-2 py-1.5 text-left">Nombre</th><th className="px-2 py-1.5">Cédula</th>
                    <th className="px-2 py-1.5">N° IPS</th><th className="px-2 py-1.5">Días</th><th className="px-2 py-1.5">Aporte</th>
                  </tr></thead>
                  <tbody>
                    {preview.valid_rows.map((r, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">{r.nombre}</td><td className="px-2 py-1.5 text-center">{r.cedula}</td>
                        <td className="px-2 py-1.5 text-center">{r.n_ips}</td><td className="px-2 py-1.5 text-center">{r.dias_reportados}</td>
                        <td className="px-2 py-1.5 text-right">{formatPYGCompact(r.monto_aporte)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={handleConfirmImport} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm" data-testid="import-ips-confirm-btn">
                {loading ? <Loader2 size={16} className="animate-spin inline" /> : `Confirmar importación (${preview.summary.valid} registros)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function IpsComparacionTab() {
  const now = new Date();
  const defaultMes = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [mes, setMes] = useState(defaultMes);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");

  const fetchComparacion = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/hr/ips/comparacion`, { withCredentials: true, params: { mes } });
      setData(data);
    } catch { toast.error("Error al cargar la comparación IPS"); }
    finally { setLoading(false); }
  }, [mes]);
  useEffect(() => { fetchComparacion(); }, [fetchComparacion]);

  const items = (data?.items || []).filter(i => filtroEstado === "todos" || i.estado === filtroEstado);

  return (
    <div className="space-y-4" data-testid="ips-comparacion-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <label className={labelCls}>Mes</label>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)} className={inputCls} data-testid="ips-mes-input" />
        </div>
        <button onClick={fetchComparacion} className="mt-4 p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50" data-testid="ips-refresh-btn"><RefreshCw size={15} /></button>
        <button onClick={() => setShowImport(true)} className="mt-4 flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-lg text-xs font-semibold" data-testid="ips-importar-btn">
          <FileSpreadsheet size={14} /> Importar extracto IPS
        </button>
        {!data?.tiene_extracto && !loading && (
          <span className="mt-4 text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded-lg">Sin extracto de IPS cargado para {mes}</span>
        )}
      </div>

      {!loading && data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(ESTADO_LABEL).map(([k, label]) => (
            <button key={k} onClick={() => setFiltroEstado(filtroEstado === k ? "todos" : k)}
              className={`rounded-xl border p-3 text-left transition-all ${filtroEstado === k ? "border-[#2E6DA4] ring-2 ring-[#2E6DA4]/20" : "border-gray-200/60"}`}
              data-testid={`ips-count-${k}`}>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-lg font-bold text-gray-900">{data.counts[k] ?? 0}</p>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Trabajador</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Días Partes</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Días IPS</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Diferencia</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Estado</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8"><Loader2 className="animate-spin mx-auto text-gray-300" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-gray-400">Sin registros para este filtro</td></tr>
            ) : items.map((i, idx) => (
              <tr key={i.worker_id || idx} className="border-t border-gray-50" data-testid={`ips-row-${i.worker_id || idx}`}>
                <td className="px-4 py-2.5 font-medium text-gray-800">{i.worker_name}</td>
                <td className="px-4 py-2.5 text-center tabular-nums">{i.dias_partes}</td>
                <td className="px-4 py-2.5 text-center tabular-nums">{i.dias_ips}</td>
                <td className={`px-4 py-2.5 text-center tabular-nums font-semibold ${i.diferencia !== 0 ? "text-amber-600" : "text-gray-400"}`}>{i.diferencia > 0 ? `+${i.diferencia}` : i.diferencia}</td>
                <td className="px-4 py-2.5 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ESTADO_BADGE[i.estado]}`}>{ESTADO_LABEL[i.estado]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ImportIpsModal open={showImport} mes={mes} onClose={() => setShowImport(false)} onDone={fetchComparacion} />
    </div>
  );
}
